import { createContext, use, useEffect, useRef, useState, type ReactNode } from 'react';
import { m } from 'motion/react';
import { Box, Icon, PlainButton, Spinner, Text } from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import { ShimmerText } from '@/components/feedback/shimmer-text';
import * as styles from './index.css';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ChainOfThoughtContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const ChainOfThoughtContext = createContext<ChainOfThoughtContextValue | null>(null);

function useChainOfThought(): ChainOfThoughtContextValue {
  const context = use(ChainOfThoughtContext);
  if (context == null) {
    throw new Error('Chain of Thought sub-components must be used within <ChainOfThought>');
  }
  return context;
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export type StepStatus = 'complete' | 'active' | 'pending' | 'error';

interface ChainOfThoughtProps {
  /** Whether content is actively streaming/executing. Auto-opens on start, auto-closes on finish. */
  isStreaming?: boolean;
  /** Default open state for non-streaming usage. */
  defaultOpen?: boolean;
  children: ReactNode;
}

export function ChainOfThought({ isStreaming = false, defaultOpen = false, children }: ChainOfThoughtProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen || isStreaming);
  const wasStreamingReference = useRef(isStreaming);

  useEffect(() => {
    if (isStreaming) {
      wasStreamingReference.current = true;
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
      setIsOpen(true);
    } else if (wasStreamingReference.current) {
      wasStreamingReference.current = false;
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
      setIsOpen(false);
    }
  }, [isStreaming]);

  return (
    <ChainOfThoughtContext.Provider value={{ isOpen, setIsOpen }}>
      <Box className={styles.container}>{children}</Box>
    </ChainOfThoughtContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Header / Trigger
// ---------------------------------------------------------------------------

interface ChainOfThoughtHeaderProps {
  children?: ReactNode;
  /** When 'error', shows a red error icon and tints the label so the failure is visible while collapsed. */
  status?: StepStatus;
}

export function ChainOfThoughtHeader({ children = 'Chain of Thought', status }: ChainOfThoughtHeaderProps) {
  const { isOpen, setIsOpen } = useChainOfThought();
  const isError = status === 'error';

  return (
    <Box
      className={styles.trigger}
      onClick={() => setIsOpen(!isOpen)}
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setIsOpen(!isOpen);
        }
      }}
    >
      {isError ? <Icon name="error" size="sm" color="critical" /> : null}
      <Text color={isError ? 'critical' : 'subtle'} size="s">
        {children}
      </Text>
      <m.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.2 }}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <Icon name="chevron-down" size="xs" color="subtle" />
      </m.div>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Content (animated collapse)
// ---------------------------------------------------------------------------

interface ChainOfThoughtContentProps {
  children: ReactNode;
}

export function ChainOfThoughtContent({ children }: ChainOfThoughtContentProps) {
  const { isOpen } = useChainOfThought();

  return (
    <m.div
      initial={false}
      animate={{
        height: isOpen ? 'auto' : 0,
        opacity: isOpen ? 1 : 0,
      }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      style={{ overflow: 'hidden' }}
    >
      <Box className={styles.stepList}>{children}</Box>
    </m.div>
  );
}

// ---------------------------------------------------------------------------
// Step
// ---------------------------------------------------------------------------

interface ChainOfThoughtStepProps {
  /** Icon to show beside the step label. */
  icon?: IconName;
  /** Step label text. */
  label: string;
  /** Optional inline badge rendered next to the label. */
  badge?: ReactNode;
  /** Optional description below the label. */
  description?: string;
  /** Visual status of this step. */
  status?: StepStatus;
  /** When true, children are collapsed by default behind a "Show details" toggle. */
  expandable?: boolean;
  /** Nested content (sources, images, text). */
  children?: ReactNode;
}

type StepIconProps = { status: StepStatus; icon?: IconName };

function StepIcon({ status, icon }: StepIconProps) {
  if (status === 'active') return <Spinner style={{ scale: '0.75' }} />;
  if (status === 'error') return <Icon name="error" size="sm" color="critical" />;
  if (status === 'complete') {
    return icon != null ? (
      <Icon name={icon} size="sm" color="subtle" />
    ) : (
      <Icon name="check-circle" size="sm" color="success" />
    );
  }
  return <Icon name="check-circle" size="sm" color="subtle" />;
}

function stepLabelTextAs(status: StepStatus): typeof ShimmerText | 'span' {
  return status === 'active' ? ShimmerText : 'span';
}

function stepLabelColor(status: StepStatus): 'subtle' | 'base' {
  return status === 'pending' ? 'subtle' : 'base';
}

function stepDescriptionColor(status: StepStatus): 'critical' | 'subtle' {
  return status === 'error' ? 'critical' : 'subtle';
}

function stepToggleLabel(isExpanded: boolean): string {
  return isExpanded ? 'Hide details' : 'Show details';
}

export function ChainOfThoughtStep({
  icon,
  label,
  badge,
  description,
  status = 'complete',
  expandable = false,
  children,
}: ChainOfThoughtStepProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = children != null;
  const showExpandToggle = expandable && hasChildren;

  return (
    <Box className={styles.step}>
      <Box className={styles.stepHeader}>
        <Box
          style={{ width: 20, height: 20 }}
          flexShrink="0"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StepIcon status={status} icon={icon} />
        </Box>
        <Box display="flex" flexDirection="column" gap="025" flexGrow="1">
          <Box display="flex" alignItems="center" gap="075" flexWrap="wrap">
            <Text as={stepLabelTextAs(status)} color={stepLabelColor(status)} size="s">
              {label}
            </Text>
            {badge}
          </Box>
          {description != null ? (
            <Text color={stepDescriptionColor(status)} size="xs">
              {description}
            </Text>
          ) : null}
        </Box>
        {showExpandToggle ? (
          <PlainButton
            display="flex"
            alignItems="center"
            gap="025"
            flexShrink="0"
            onClick={() => setIsExpanded((previous) => !previous)}
            aria-expanded={isExpanded}
            className={styles.detailsToggle}
          >
            <Text size="xs" color="subtle">
              {stepToggleLabel(isExpanded)}
            </Text>
            <m.div
              animate={{ rotate: isExpanded ? 90 : 0 }}
              transition={{ duration: 0.15 }}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Icon name="chevron-right" size="xs" color="subtle" />
            </m.div>
          </PlainButton>
        ) : null}
      </Box>
      {hasChildren ? (
        expandable ? (
          <m.div
            initial={false}
            animate={{
              height: isExpanded ? 'auto' : 0,
              opacity: isExpanded ? 1 : 0,
            }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <Box className={styles.stepContent}>{children}</Box>
          </m.div>
        ) : (
          <Box className={styles.stepContent}>{children}</Box>
        )
      ) : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sources (badge list)
// ---------------------------------------------------------------------------

interface ChainOfThoughtSourcesProps {
  children: ReactNode;
}

export function ChainOfThoughtSources({ children }: ChainOfThoughtSourcesProps) {
  return <Box className={styles.sourceList}>{children}</Box>;
}

interface ChainOfThoughtSourceProps {
  href?: string;
  children: ReactNode;
}

export function ChainOfThoughtSource({ href, children }: ChainOfThoughtSourceProps) {
  if (href != null) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={styles.sourceBadge}>
        {children}
      </a>
    );
  }
  return <Box className={styles.sourceBadge}>{children}</Box>;
}

// Re-export BashDetail for use in tool call rendering
export { BashDetail } from './bash-detail';
